import { useCallback, useMemo, useState } from "react";

import { useLocalization } from "@fluent/react";
import { useNavigate } from "react-router-dom";
import { Row, useBreakpoints } from "@narmi/design_system";
import { useLocalStorage } from "usehooks-ts";
import Account from "byzantine/src/Account";
import { modules } from "byzantine";
import cc from "classcat";

import { useAccountsContext } from "../../../components/contexts/AccountContext";
import { useCurrentUser } from "../../../components/contexts/CurrentUserContext";
import { VerifyAccountDialog } from "../../../components/settings/linked_accounts/ExternalAccountManager";
import {
  DASHBOARD_LINKED_ACCOUNTS_BASENAME,
  VERIFYROUTES,
  useIsAddAccountsDialogOpen,
} from "../../../components/settings/linked_accounts/hooks";
import AddAccountModal from "../../../components/transfer/AddAccountModal";
import { SudoProvider } from "../../../components/SudoContext";
import SudoDialog from "../../../components/SudoDialog";
import { createFNVHashSync } from "../../../components/authentication/utils";
import NoLinkedAccounts from "../dashboard/ExternalAccounts/ExternalAccountsTopRow/NoLinkedAccounts";
import { useRefreshStaleExternalAccounts } from "../dashboard/ExternalAccounts/hooks/useRefreshExternalAccounts";
import { DAYS_UNTIL_DISPLAYED_AGAIN } from "../dashboard/ExternalAccounts/ExternalAccountsContainer/constants";
import { mergeAccountsById } from "../dashboard/ExternalAccounts/ExternalAccountsContainer/utils";
import { useLegacyHiddenExternalAccounts } from "../dashboard/ExternalAccounts/useLegacyHiddenExternalAccounts";

import styles from "./AccountsModule.module.scss";
import AccountListRow from "./AccountListRow";
import useViewPreference from "./useViewPreference";
import AccountsSectionHeader from "./AccountsSectionShared/AccountsSectionHeader";
import AccountsList from "./AccountsSectionShared/AccountsList";
import CollapsibleListGroup from "./AccountsSectionShared/CollapsibleListGroup";
import HiddenAccountsSection from "./AccountsSectionShared/HiddenAccountsSection";

import type { AddAccountMode } from "../../../components/settings/linked_accounts/types";
import type { AccountClassType } from "./AccountsSectionShared/types";

type LinkedAccountsSectionProps = {
  additionalExternalAccounts?: AccountClassType[];
};

const handleNavigateToExternalAccountsPage = () => {
  window.location.assign("/settings/linked_accounts");
};

const LinkedAccountsSection = ({
  additionalExternalAccounts = [],
}: LinkedAccountsSectionProps) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { m: isMediumViewport } = useBreakpoints();
  const isSmall = !isMediumViewport;
  const {
    externalAccounts: contextExternalAccounts,
    updateAccount,
    setAccounts,
  } = useAccountsContext();
  const externalAccounts = useMemo(() => {
    const byId = new Map(contextExternalAccounts.map((a) => [a.id, a]));
    additionalExternalAccounts.forEach((a) => {
      if (!byId.has(a.id)) byId.set(a.id, a);
    });
    return Array.from(byId.values());
  }, [contextExternalAccounts, additionalExternalAccounts]);
  const { currentUser } = useCurrentUser();
  const hasExternalAccountCreatePermission =
    modules.externalAccounts.useAddExternalAccountPermission();
  const [externalView, setExternalView] = useViewPreference("external");

  const userId = currentUser?.id?.toString() || "";
  const legacyHiddenExternalAccountIds = useLegacyHiddenExternalAccounts(
    userId,
    externalAccounts,
    updateAccount
  );
  const dismissedStorageKey = `externalAccountsSectionDismissedUntil_${createFNVHashSync(
    userId
  )}`;
  const [dismissed, setDismissed] = useLocalStorage<string | undefined>(
    dismissedStorageKey,
    undefined
  );
  const isDismissedRecently = Boolean(
    dismissed && new Date(dismissed).getTime() > Date.now()
  );
  const [addAccountMode, setAddAccountMode] =
    useState<AddAccountMode>("default");
  const isAddAccountsDialogOpen = useIsAddAccountsDialogOpen(
    DASHBOARD_LINKED_ACCOUNTS_BASENAME
  );

  const handleOpenAddAccountDialog = useCallback(() => {
    if (!hasExternalAccountCreatePermission) return;
    setAddAccountMode("default");
    navigate(
      `/${DASHBOARD_LINKED_ACCOUNTS_BASENAME}/${VERIFYROUTES.verifyOptions}`
    );
  }, [navigate, hasExternalAccountCreatePermission]);

  const handleCloseAddAccountDialog = useCallback(() => {
    setAddAccountMode("default");
    navigate("/dashboard");
  }, [navigate]);

  const handleDismissLinkedAccountsSection = useCallback(() => {
    const date = new Date();
    date.setDate(date.getDate() + DAYS_UNTIL_DISPLAYED_AGAIN);
    setDismissed(date.toString());
  }, [setDismissed]);

  const updateAccountsOnVerify = useCallback(
    (updatedAccounts: API.ExternalAccount[]) => {
      const deserializedAccounts = updatedAccounts.map((account) =>
        Account.deserialize(account)
      );
      setAccounts((accounts: AccountClassType[]) =>
        mergeAccountsById(accounts, deserializedAccounts)
      );
    },
    [setAccounts]
  );

  const handleRefreshedExternalAccounts = useCallback(
    (refreshedAccounts: AccountClassType[]) => {
      setAccounts((accounts: AccountClassType[]) =>
        mergeAccountsById(accounts, refreshedAccounts)
      );
    },
    [setAccounts]
  );

  useRefreshStaleExternalAccounts({
    externalAccounts,
    onRefresh: handleRefreshedExternalAccounts,
  });

  // On small screens, always use list view
  const effectiveExternalView = isSmall ? "list" : externalView;
  const [isEditingExternal, setIsEditingExternal] = useState(false);

  const [selectedAccount, setSelectedAccount] =
    useState<AccountClassType | null>(null);
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);

  const openVerifyDialog = useCallback((account: AccountClassType) => {
    setSelectedAccount(account);
    setIsVerifyDialogOpen(true);
  }, []);

  const handleTriggerInstantVerification = useCallback(
    (mode: AddAccountMode) => {
      setAddAccountMode(mode);
      navigate(
        `/${DASHBOARD_LINKED_ACCOUNTS_BASENAME}/${VERIFYROUTES.verifyOptions}`
      );
    },
    [navigate]
  );

  const toggleEditExternal = useCallback(() => {
    setIsEditingExternal((prev) => !prev);
  }, []);

  const isHidden = (a: AccountClassType) =>
    a.hidden || legacyHiddenExternalAccountIds.has(createFNVHashSync(a.id));
  const visibleExternalAccounts = externalAccounts.filter((a) => !isHidden(a));
  const hiddenExternalAccounts = externalAccounts.filter(isHidden);
  const hasHiddenExternalAccountsToShow =
    !isSmall && hiddenExternalAccounts.length > 0;

  return (
    <>
      {/* Linked Accounts */}
      {(visibleExternalAccounts.length > 0 ||
        hasHiddenExternalAccountsToShow) && (
        <div className={styles.linkedSection}>
          <AccountsSectionHeader
            title={l10n.getString(
              "dashboard-redesign-accounts-linked-accounts"
            )}
            view={effectiveExternalView}
            onViewChange={setExternalView}
            isEditing={isEditingExternal}
            onEditToggle={toggleEditExternal}
            isSmall={isSmall}
            onTitleClick={handleNavigateToExternalAccountsPage}
          />
          {effectiveExternalView === "list" ? (
            <div className={styles.listContainer}>
              {visibleExternalAccounts.map((account) => (
                <AccountListRow
                  key={account.id}
                  account={account}
                  isEditing={isEditingExternal}
                  isSmall={isSmall}
                  onVerify={openVerifyDialog}
                  onTriggerInstantVerification={
                    handleTriggerInstantVerification
                  }
                />
              ))}
              {hasHiddenExternalAccountsToShow && (
                <CollapsibleListGroup
                  title={l10n.getString(
                    "dashboard-redesign-accounts-hidden-title"
                  )}
                  accounts={hiddenExternalAccounts}
                  onVerify={openVerifyDialog}
                  onTriggerInstantVerification={
                    handleTriggerInstantVerification
                  }
                />
              )}
            </div>
          ) : (
            <>
              {visibleExternalAccounts.length > 0 ? (
                <AccountsList
                  accounts={visibleExternalAccounts}
                  view={effectiveExternalView}
                  isEditing={isEditingExternal}
                  onVerify={openVerifyDialog}
                  onTriggerInstantVerification={
                    handleTriggerInstantVerification
                  }
                />
              ) : (
                <p className="fontSize--s fontColor--secondary padding--top--s">
                  {l10n.getString("dashboard-redesign-accounts-no-accounts")}
                </p>
              )}
              {isEditingExternal && (
                <HiddenAccountsSection
                  accounts={hiddenExternalAccounts}
                  view={effectiveExternalView}
                  onVerify={openVerifyDialog}
                  onTriggerInstantVerification={
                    handleTriggerInstantVerification
                  }
                />
              )}
            </>
          )}
        </div>
      )}
      {visibleExternalAccounts.length === 0 &&
        !hasHiddenExternalAccountsToShow &&
        hasExternalAccountCreatePermission && (
          <div className={cc([styles.linkedSection, "padding--top--xs"])}>
            <Row
              className="padding--top--s"
              justifyContent="space-between"
              gapSize="none"
            >
              <NoLinkedAccounts
                isVisible={!isDismissedRecently}
                onLinkAccount={handleOpenAddAccountDialog}
                onClose={handleDismissLinkedAccountsSection}
              />
            </Row>
            {isDismissedRecently && (
              <p className="fontSize--s fontColor--secondary padding--top--s">
                {l10n.getString("dashboard-redesign-accounts-no-accounts")}
              </p>
            )}
          </div>
        )}
      {selectedAccount && (
        <VerifyAccountDialog
          account={selectedAccount}
          isDialogOpen={isVerifyDialogOpen}
          setDialogOpen={setIsVerifyDialogOpen}
          callback={updateAccount}
          uatMicrodepAmntOne={undefined}
          uatMicrodepAmntTwo={undefined}
        />
      )}

      <SudoProvider basename={DASHBOARD_LINKED_ACCOUNTS_BASENAME}>
        <AddAccountModal
          open={isAddAccountsDialogOpen}
          handleClose={handleCloseAddAccountDialog}
          updateAccounts={updateAccountsOnVerify}
          basename={DASHBOARD_LINKED_ACCOUNTS_BASENAME}
          addAccountMode={addAccountMode}
        />
        <SudoDialog />
      </SudoProvider>
    </>
  );
};

export default LinkedAccountsSection;
